import React, { useState } from 'react'
import {
  BookOpenIcon,
  BrainIcon,
  HeartIcon,
  ActivityIcon,
  EyeIcon,
  HandIcon,
  MessageSquareIcon,
  UserIcon,
  FilterIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  ClipboardIcon,
  PlusIcon,
  AlertTriangleIcon,
  InfoIcon,
} from 'lucide-react'

interface PresentLevelData {
  type: string  // "academic", "functional", "Strength", "Need", "Assessment Result", "Parent Concern", etc.
  subject?: string  // "math", "reading", "speech", etc.
  source_page: number
  evaluation_source?: string  // assessment or evaluation reference
  short_description: string
  reasoning: string  // internal explanation
  full_text: string  // full text excerpt
}

interface PresentLevelRendererProps {
  present_levels: {
    levels: PresentLevelData[]
  }
  onSourceClick?: (pageNumber: number) => void
}

const PresentLevelRenderer: React.FC<PresentLevelRendererProps> = ({
  present_levels,
  onSourceClick
}) => {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({})
  const [activeFilter, setActiveFilter] = useState<string>('All')

  // Transform data to match the elegant structure
  const levels = present_levels.levels.map((level, index) => ({
    type: getStandardizedType(level.type),
    subject: level.subject || 'General',
    full_text: level.full_text,
    short_description: level.short_description,
    source_page: level.source_page,
    reasoning: level.reasoning,
    evaluation_source: level.evaluation_source,
    originalIndex: index
  }))

  // Get unique subjects for filtering
  const subjects = [
    'All',
    ...Array.from(new Set(levels.map((level) => level.subject))).sort(),
  ]

  // Filter levels based on active filter
  const filteredLevels = activeFilter === 'All'
    ? levels
    : levels.filter((level) => level.subject === activeFilter)

  const toggleExpand = (index: number) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const getSubjectIcon = (subject: string) => {
    const lowerSubject = subject.toLowerCase()
    switch (true) {
      case lowerSubject.includes('reading') || lowerSubject.includes('literacy'):
      case lowerSubject.includes('writing'):
      case lowerSubject.includes('english'):
        return <BookOpenIcon className="h-4 w-4 text-blue-600" />
      case lowerSubject.includes('math') || lowerSubject.includes('mathematics'):
        return <ActivityIcon className="h-4 w-4 text-purple-600" />
      case lowerSubject.includes('cognitive') || lowerSubject.includes('thinking'):
        return <BrainIcon className="h-4 w-4 text-amber-600" />
      case lowerSubject.includes('social') || lowerSubject.includes('emotional'):
        return <HeartIcon className="h-4 w-4 text-red-600" />
      case lowerSubject.includes('vision') || lowerSubject.includes('visual'):
        return <EyeIcon className="h-4 w-4 text-green-600" />
      case lowerSubject.includes('occupational') || lowerSubject.includes('ot'):
        return <HandIcon className="h-4 w-4 text-indigo-600" />
      case lowerSubject.includes('speech') || lowerSubject.includes('language'):
        return <MessageSquareIcon className="h-4 w-4 text-teal-600" />
      case lowerSubject.includes('physical') || lowerSubject.includes('motor'):
        return <ActivityIcon className="h-4 w-4 text-orange-600" />
      default:
        return <UserIcon className="h-4 w-4 text-gray-600" />
    }
  }

  const renderSourceReference = (pageNumber: number) => {
    if (!onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSourceClick(pageNumber)
        }}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <FileTextIcon className="h-3 w-3 mr-1" />
        Page {pageNumber}
      </button>
    )
  }

  // Group levels by type
  const strengths = filteredLevels.filter((level) => level.type === 'Strength')
  const needs = filteredLevels.filter((level) => level.type === 'Need')
  const assessmentResults = filteredLevels.filter((level) => level.type === 'Assessment Result')
  const parentConcerns = filteredLevels.filter((level) => level.type === 'Parent Concern')

  if (!levels || levels.length === 0) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg">
        No present levels found
      </div>
    )
  }

  const sections = [
    {
      title: 'Strengths',
      items: strengths,
      icon: <PlusIcon className="h-4 w-4" />,
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      titleColor: 'text-green-700',
      iconBgColor: 'bg-green-100'
    },
    {
      title: 'Areas of Need', 
      items: needs,
      icon: <AlertTriangleIcon className="h-4 w-4" />,
      color: 'amber',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200', 
      titleColor: 'text-amber-700',
      iconBgColor: 'bg-amber-100'
    },
    {
      title: 'Assessment Results',
      items: assessmentResults,
      icon: <InfoIcon className="h-4 w-4" />,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      titleColor: 'text-blue-700', 
      iconBgColor: 'bg-blue-100'
    },
    {
      title: 'Parent Concerns',
      items: parentConcerns,
      icon: <UserIcon className="h-4 w-4" />,
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      titleColor: 'text-purple-700',
      iconBgColor: 'bg-purple-100'
    }
  ]

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Present Levels of Performance
        </h2>
        <p className="text-gray-600 text-sm">
          Academic and functional performance information from the student's IEP
        </p>
      </div>

      {/* Subject filters */}
      <div className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-blue-100 mr-3">
            <FilterIcon className="h-4 w-4 text-blue-600" />
          </div>
          <span className="text-sm font-semibold text-gray-900">
            Filter by Subject Area
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {subjects.map((subject) => (
            <button
              key={subject}
              onClick={() => setActiveFilter(subject)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeFilter === subject
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
              }`}
            >
              {subject}
            </button>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {sections.map((section) => {
          if (section.items.length === 0) return null

          return (
            <div key={section.title} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              {/* Section Header */}
              <div className={`${section.bgColor} px-6 py-4 border-b ${section.borderColor}`}>
                <h3 className={`text-lg font-semibold ${section.titleColor} flex items-center`}>
                  <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${section.iconBgColor} ${section.titleColor} mr-3`}>
                    {section.icon}
                  </span>
                  {section.title}
                  <span className="ml-3 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-gray-600 bg-white rounded-full">
                    {section.items.length}
                  </span>
                </h3>
              </div>

              {/* Section Content */}
              <div className="p-6">
                <div className="space-y-4">
                  {section.items.map((level) => (
                    <div
                      key={level.originalIndex}
                      className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex items-start justify-between p-4 bg-white border-b border-gray-200">
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium text-sm leading-relaxed mb-3">
                            {level.short_description}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                              {getSubjectIcon(level.subject)}
                              <span className="ml-1">{level.subject}</span>
                            </span>
                            {level.evaluation_source && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-full">
                                <ClipboardIcon className="h-3 w-3 mr-1" />
                                {level.evaluation_source}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleExpand(level.originalIndex)}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800 ml-4 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          {expandedItems[level.originalIndex] ? (
                            <>
                              <ChevronDownIcon className="h-4 w-4 mr-1" />
                              Less
                            </>
                          ) : (
                            <>
                              <ChevronRightIcon className="h-4 w-4 mr-1" />
                              Details
                            </>
                          )}
                        </button>
                      </div>

                      {expandedItems[level.originalIndex] && (
                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                          <div className="space-y-4">
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="text-sm font-medium text-gray-700">Full Text from IEP</h5>
                                {renderSourceReference(level.source_page)}
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                                  {level.full_text}
                                </p>
                              </div>
                            </div>
                            {level.reasoning && (
                              <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Analysis Notes</h5>
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                  <p className="text-sm text-blue-800 leading-relaxed italic">
                                    {level.reasoning}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {filteredLevels.length === 0 && (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <FilterIcon className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg font-medium mb-2">
            No present levels found
          </p>
          <p className="text-gray-400 text-sm">
            Try adjusting your filter to see more results
          </p>
        </div>
      )}
    </div>
  )
}

// Helper function to standardize types from different formats
function getStandardizedType(type: string): string {
  const lowerType = type.toLowerCase()
  
  if (lowerType.includes('strength')) return 'Strength'
  if (lowerType.includes('need') || lowerType.includes('weakness')) return 'Need'
  if (lowerType.includes('assessment') || lowerType.includes('result')) return 'Assessment Result'
  if (lowerType.includes('parent') || lowerType.includes('concern')) return 'Parent Concern'
  if (lowerType.includes('academic')) return 'Assessment Result' // Map academic to assessment results
  if (lowerType.includes('functional')) return 'Assessment Result' // Map functional to assessment results
  
  return 'Assessment Result' // Default fallback
}

export default PresentLevelRenderer
